/**
 * Score a value against a search query for relevance-based sorting.
 *
 * This is the shared search-matching backbone used across the app: it is the
 * default filter for the cmdk `Command` wrapper (so every command palette /
 * combobox built on it inherits this behaviour) and the engine behind
 * `filterAndSortBySearch` for manual client-side lists.
 *
 * Beyond plain substring matching it adds the flexibility users expect from an
 * autocomplete:
 *  - **Multi-token queries** — the query is split on whitespace and every token
 *    must match somewhere in the value (in any order). This lets a value be
 *    found by a mixture of words drawn from different fields (e.g. a module
 *    name + a page name combined into one searchable string).
 *  - **Singular/plural folding** — "category" matches "Categories" and vice
 *    versa via lightweight English stemming.
 *  - **Small typos** — a token within a short edit distance of a value word
 *    still matches (e.g. "messging" → "Messaging").
 *
 * Returns a relevance score where higher is more relevant and `0` means the
 * item should be hidden. Exact matches always outrank stem matches, which
 * always outrank fuzzy matches:
 *  0    — no match (hide)
 *  ~0.5 — typo / fuzzy match
 *  ~0.7-0.9 — singular/plural (stem) match
 *  1    — substring/contains match (e.g. "Pro*cu*rement" for "cu")
 *  2    — non-first word prefix match (e.g. "File *Man*agement" for "man")
 *  3    — first word prefix match (e.g. "*Mes*saging" for "mes")
 *
 * For multi-token queries the score is the average of the per-token scores, so
 * the tiers above still describe the single-token (and best-case) behaviour.
 */

/** Tokens shorter than this are never fuzzy-matched (too noisy). */
const FUZZY_MIN_LEN = 4;

/**
 * Reduce an English word to a rough singular stem so plural and singular forms
 * compare equal. Deliberately small and rule-based — not a full stemmer — but
 * enough for entity/page/module names ("categories"→"category",
 * "boxes"→"box", "products"→"product"; "address" stays "address").
 */
export function singularize(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith('ies') && word.length > 4) return `${word.slice(0, -3)}y`;
  if (/(ses|xes|zes|ches|shes)$/.test(word)) return word.slice(0, -2);
  // Don't strip a trailing -s from words that aren't English plurals — doing so
  // would produce spurious stem matches. Covers -ss (address, boss), -us
  // (status, focus, bonus, porous), and -is/-sis/-xis (analysis, axis, basis).
  if (/(ss|us|is)$/.test(word)) return word;
  if (word.endsWith('s')) return word.slice(0, -1);
  return word;
}

/** Levenshtein edit distance between two strings (iterative two-row DP). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** Score a single query token against a pre-tokenized value. */
function tokenScore(
  valueLower: string,
  valueWords: string[],
  valueStems: string[],
  token: string,
): number {
  // Exact tiers (highest priority — preserves the historic 3/2/1 contract).
  if (valueWords[0]?.startsWith(token)) return 3;
  for (let i = 1; i < valueWords.length; i++) {
    if (valueWords[i].startsWith(token)) return 2;
  }
  if (valueLower.includes(token)) return 1;

  // Singular/plural (stem) tiers — ranked just below exact substring matches.
  const stem = singularize(token);
  if (valueStems[0]?.startsWith(stem)) return 0.9;
  for (let i = 1; i < valueStems.length; i++) {
    if (valueStems[i].startsWith(stem)) return 0.8;
  }
  if (valueStems.some((w) => w.includes(stem))) return 0.7;

  // Typo / fuzzy tier — lowest positive score, conservative thresholds.
  if (token.length >= FUZZY_MIN_LEN) {
    const maxDist = token.length >= 8 ? 2 : 1;
    for (const w of valueWords) {
      if (w.length < FUZZY_MIN_LEN) continue;
      if (Math.abs(w.length - token.length) <= maxDist && levenshtein(w, token) <= maxDist) {
        return 0.5;
      }
      // Fuzzy prefix: the start of a longer word is close to the token.
      if (w.length > token.length && levenshtein(w.slice(0, token.length), token) <= maxDist) {
        return 0.45;
      }
    }
  }

  return 0;
}

export function searchScore(value: string, search: string, keywords?: string[]): number {
  const searchLower = search.toLowerCase().trim();
  if (!searchLower) return 1; // show everything when search is empty

  // cmdk passes optional keywords as a third arg — fold them into the haystack
  // so items can be found by aliases they declare.
  const haystack = keywords && keywords.length > 0 ? `${value} ${keywords.join(' ')}` : value;
  const valueLower = haystack.toLowerCase();
  const valueWords = valueLower.split(/\s+/).filter(Boolean);
  if (valueWords.length === 0) return 0;
  const valueStems = valueWords.map(singularize);

  // Every query token must match somewhere; the overall score is the average
  // of the per-token scores so values that match more strongly rank higher.
  // Note: scores can exceed cmdk's documented [0,1] range (the historic 3/2/1
  // tiers predate this util being the cmdk filter); cmdk sorts by raw numeric
  // comparison so larger-is-better holds. Keep returns monotonic, not clamped.
  const tokens = searchLower.split(/\s+/).filter(Boolean);
  let total = 0;
  for (const token of tokens) {
    const score = tokenScore(valueLower, valueWords, valueStems, token);
    if (score === 0) return 0;
    total += score;
  }
  return total / tokens.length;
}

/**
 * Filter and sort an array of items using prefix-prioritized search scoring.
 *
 * @param items - The items to filter
 * @param search - The search query string
 * @param getText - Function to extract the searchable text from an item
 * @returns Filtered and sorted array (prefix matches first, then substring matches)
 */
export function filterAndSortBySearch<T>(
  items: T[],
  search: string,
  getText: (item: T) => string,
): T[] {
  if (!search.trim()) return items;

  const scored = items
    .map((item) => ({ item, score: searchScore(getText(item), search) }))
    .filter(({ score }) => score > 0);

  scored.sort((a, b) => b.score - a.score);

  return scored.map(({ item }) => item);
}
